import { useState, useEffect } from "react";
import { API , authFetch, useEditMode, useAuth } from "@/App";
import { toast } from "sonner";
import { Wallet, Plus, Search, Check, X, MoreHorizontal, Eye, Camera, Image } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { CollectionDetailDialog } from "@/components/CollectionDetailDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const CollectionsPage = () => {
  const canEdit = useEditMode();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [collections, setCollections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    employee_id: "",
    party_id: "",
    amount: 0,
    mode: "CASH",
    reference_no: "",
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null); // collection object

  useEffect(() => {
    fetchData();
  }, [statusFilter, modeFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/collections?`;
      if (statusFilter && statusFilter !== "all") url += `status=${statusFilter}&`;
      if (modeFilter && modeFilter !== "all") url += `mode=${modeFilter}&`;

      const [collectionsRes, employeesRes, partiesRes] = await Promise.all([
        authFetch(url, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
        authFetch(`${API}/parties?approval_status=APPROVED`, {  }),
      ]);

      const collectionsData = await collectionsRes.json();
      const employeesData = await employeesRes.json();
      const partiesData = await partiesRes.json();

      if (collectionsData.success) setCollections(collectionsData.data);
      if (employeesData.success) setEmployees(employeesData.data);
      if (partiesData.success) setParties(partiesData.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let photos = [];
      if (photoFiles.length > 0) {
        setUploadingPhotos(true);
        const fd = new FormData();
        photoFiles.forEach(f => fd.append("files", f));
        const uploadRes = await authFetch(`${API}/upload-multiple?category=collections&entity_id=${formData.party_id}`, { method: "POST", body: fd });
        const uploadData = await uploadRes.json();
        if (uploadData.success) photos = uploadData.data.map(d => d.storage_path);
        setUploadingPhotos(false);
      }
      const response = await authFetch(`${API}/collections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          ...formData,
          amount: parseInt(formData.amount) * 100,
          photos,
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Collection recorded");
        setIsDialogOpen(false);
        resetForm();
        setPhotoFiles([]);
        fetchData();
      } else {
        toast.error(data.error || "Operation failed");
      }
    } catch (error) {
      toast.error("Failed to record collection");
    }
  };

  const handleValidate = async (collectionId) => {
    try {
      const response = await authFetch(`${API}/collections/${collectionId}/validate`, {
        method: "POST",
        
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Collection validated");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to validate collection");
    }
  };

  const handleReject = async (collectionId) => {
    try {
      const response = await authFetch(`${API}/collections/${collectionId}/reject`, {
        method: "POST",
        
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Collection rejected");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to reject collection");
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      employee_id: "",
      party_id: "",
      amount: 0,
      mode: "CASH",
      reference_no: "",
    });
  };

  const formatCurrency = (paise) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(paise / 100);
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: "badge-warning",
      VALIDATED: "badge-success",
      REJECTED: "badge-danger",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || "badge-default"}`}>
        {status}
      </span>
    );
  };

  const getModeBadge = (mode) => {
    const styles = {
      CASH: "bg-emerald-100 text-emerald-700 border-emerald-200",
      CHEQUE: "bg-blue-100 text-blue-700 border-blue-200",
      NEFT: "bg-purple-100 text-purple-700 border-purple-200",
      UPI: "bg-amber-100 text-amber-700 border-amber-200",
    };
    return (
      <Badge variant="outline" className={styles[mode] || "bg-slate-100 text-slate-700"}>
        {mode}
      </Badge>
    );
  };

  // Region-filtered slice — respects ALL/GJ/RJ. Falls back to dealer.region_id
  // if collection doc doesn't have its own region_id.
  const partyById = parties.reduce((acc, p) => {
    if (p?.party_id) acc[p.party_id] = p;
    return acc;
  }, {});
  const employeeById = employees.reduce((acc, e) => {
    if (e?.employee_id) acc[e.employee_id] = e;
    return acc;
  }, {});
  const regionCollections = filterByRegion(collections, (c) =>
    c.region_id || partyById[c.party_id]?.region_id || employeeById[c.collected_by]?.region_id
  );

  // Calculate totals (region-aware)
  const totalCollections = regionCollections.reduce((sum, c) => sum + c.amount, 0);
  const pendingCount = regionCollections.filter((c) => c.status === "PENDING").length;

  return (
    <div className="space-y-6" data-testid="collections-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Collections</h1>
          <p className="text-slate-500 text-sm mt-1">Track payment collections</p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="collections-region-switcher"
          />
        {/* Record Collection button removed — collections are filed from the mobile app */}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="stat-total">
          <p className="text-sm text-slate-500">Total Collections</p>
          <p className="text-2xl font-bold text-emerald-600 font-mono mt-1">
            {formatCurrency(totalCollections)}
          </p>
        </Card>
        <Card className="p-4" data-testid="stat-pending">
          <p className="text-sm text-slate-500">Pending Validation</p>
          <p className="text-2xl font-bold text-amber-600 font-mono mt-1">{pendingCount}</p>
        </Card>
        <Card className="p-4" data-testid="stat-count">
          <p className="text-sm text-slate-500">Total Records</p>
          <p className="text-2xl font-bold text-slate-900 font-mono mt-1">{collections.length}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by dealer, reference..." className="pl-10"
              data-testid="collection-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="VALIDATED">Validated</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="mode-filter">
              <SelectValue placeholder="All Modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              <SelectItem value="CASH">Cash</SelectItem>
              <SelectItem value="CHEQUE">Cheque</SelectItem>
              <SelectItem value="NEFT">NEFT</SelectItem>
              <SelectItem value="UPI">UPI</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="collections-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Dealer</th>
                <th>Employee</th>
                <th className="text-right">Amount</th>
                <th>Mode</th>
                <th>Ref No.</th>
                <th>Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : regionCollections.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8">
                    <div className="empty-state">
                      <Wallet className="empty-state-icon" />
                      <p className="empty-state-title">No collections found</p>
                      <p className="empty-state-description">
                        Record your first collection.
                      </p>
                      <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Record Collection
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                regionCollections.map((collection) => {
                  const party = parties.find((p) => p.party_id === collection.party_id);
                  const employee = employees.find((e) => e.employee_id === collection.employee_id);
                  return (
                    <tr key={collection.collection_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="font-mono text-xs">
                        <button
                          type="button"
                          onClick={() => setDetailOpen(collection)}
                          className="text-[#8B1A1A] hover:underline"
                          data-testid={`open-collection-${collection.collection_id}`}
                        >
                          {collection.collection_id}
                        </button>
                      </td>
                      <td>{collection.date}</td>
                      <td className="font-medium">{party?.firm_name || "-"}</td>
                      <td>{employee?.full_name || "-"}</td>
                      <td className="numeric font-bold text-emerald-600">
                        {formatCurrency(collection.amount)}
                      </td>
                      <td>{getModeBadge(collection.mode)}</td>
                      <td className="font-mono text-xs flex items-center gap-1.5">
                        {collection.reference_no || "-"}
                        {Array.isArray(collection.photos) && collection.photos.length > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-violet-50 text-violet-700 border border-violet-200"
                            title={`${collection.photos.length} photo${collection.photos.length > 1 ? "s" : ""}`}
                            data-testid={`photo-badge-${collection.collection_id}`}
                          >
                            <Image className="w-2.5 h-2.5" /> {collection.photos.length}
                          </span>
                        )}
                      </td>
                      <td>{getStatusBadge(collection.status)}</td>
                      <td>
                        {collection.status === "PENDING" ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-100"
                              onClick={() => setDetailOpen(collection)}
                              data-testid={`view-collection-${collection.collection_id}`}
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleValidate(collection.collection_id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(collection.collection_id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setDetailOpen(collection)}
                                data-testid={`view-details-${collection.collection_id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Collection Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>        <DialogContent className="max-w-md" data-testid="collection-dialog">
          <DialogHeader>
            <DialogTitle>Record Collection</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Mode</label>
                <Select
                  value={formData.mode}
                  onValueChange={(value) => setFormData({ ...formData, mode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="CHEQUE">Cheque</SelectItem>
                    <SelectItem value="NEFT">NEFT</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Dealer</label>
                <Select
                  value={formData.party_id}
                  onValueChange={(value) => setFormData({ ...formData, party_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select dealer" />
                  </SelectTrigger>
                  <SelectContent>
                    {parties
                      .filter((p) => p.party_type === "DEALER")
                      .map((party) => (
                        <SelectItem key={party.party_id} value={party.party_id}>
                          {party.firm_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Employee</label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Amount (₹)</label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="10000"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reference No.</label>
                <Input
                  value={formData.reference_no}
                  onChange={(e) => setFormData({ ...formData, reference_no: e.target.value })}
                  placeholder="TXN123456"
                />
              </div>
            </div>
            {/* Photo Upload */}
            <div>
              <label className="text-sm font-medium text-slate-700">Photos (optional)</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-2.5 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#8B1A1A] bg-slate-50 transition-colors" data-testid="collection-photo-upload">
                <Camera className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">{photoFiles.length > 0 ? `${photoFiles.length} file(s) selected` : "Click to upload photos"}</span>
                <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))} />
              </label>
              {photoFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {photoFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                      <Image className="w-3 h-3" /> {f.name}
                      <button type="button" onClick={() => setPhotoFiles(photoFiles.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Record Collection
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {detailOpen && (
        <CollectionDetailDialog
          open={!!detailOpen}
          onClose={() => setDetailOpen(null)}
          collection={detailOpen}
          party={parties.find((p) => p.party_id === detailOpen.party_id)}
          employee={employees.find((e) => e.employee_id === detailOpen.employee_id)}
        />
      )}
    </div>
  );
};

export default CollectionsPage;
